import { mergeProps } from "@base-ui/react";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "@/lib/utils";

const typographyVariants = cva("text-foreground", {
	variants: {
		variant: {
			h1: "text-3xl font-extrabold tracking-tight lg:text-4xl",
			h2: "border-b py-2 text-2xl font-semibold tracking-tight first:mt-0",
			h3: "text-xl font-semibold tracking-tight",
			h4: "text-lg font-semibold tracking-tight",
			lead: "text-md text-accent-foreground",
			paragraph: "text-sm leading-7 not-first:mt-6",
			large: "text-lg font-semibold",
			small: "text-xs font-medium leading-none",
			muted: "text-sm text-muted-foreground",
			inlineCode:
				"relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold",
			multilineCode:
				"relative rounded bg-muted p-4 font-mono text-sm font-semibold overflow-x-auto",
			list: "my-6 ml-6 list-disc [&>li]:mt-2",
			quote: "mt-6 border-l-2 pl-6 italic text-muted-foreground",
			md: "text-base",
			text: "text-sm",
		},
		weight: {
			normal: "font-normal",
			bold: "font-bold",
			semibold: "font-semibold",
		},
	},
	defaultVariants: {
		variant: "text",
		weight: "normal",
	},
});

export interface TypographyProps
	extends React.HTMLAttributes<HTMLElement>,
		VariantProps<typeof typographyVariants> {
	asChild?: boolean;
	as?: keyof React.JSX.IntrinsicElements;
}

const Typography = ({
	className,
	variant,
	as,
	asChild = false,
	weight = "normal",
	render,
	...props
}: useRender.ComponentProps<"div"> & TypographyProps) => {
	return useRender({
		defaultTagName: getDefaultElement(variant),
		render,
		props: mergeProps<"div">(
			{
				className: cn(typographyVariants({ variant, weight, className })),
			},
			props,
		),
	});
};

Typography.displayName = "Typography";

// Helper function to determine default element based on variant
function getDefaultElement(
	variant: TypographyProps["variant"],
): keyof React.JSX.IntrinsicElements {
	switch (variant) {
		case "h1":
			return "h1";
		case "h2":
			return "h2";
		case "h3":
			return "h3";
		case "h4":
			return "h4";
		case "lead":
		case "paragraph":
			return "p";
		case "large":
			return "div";
		case "small":
		case "muted":
			return "span";
		case "inlineCode":
			return "code";
		case "multilineCode":
			return "pre";
		case "list":
			return "ul";
		case "quote":
			return "blockquote";
		default:
			return "p";
	}
}

// Export named components for convenience
export const H1 = (props: Omit<TypographyProps, "variant" | "as">) => (
	<Typography variant="h1" {...props} />
);
H1.displayName = "H1";

export const H2 = (props: Omit<TypographyProps, "variant" | "as">) => (
	<Typography variant="h2" {...props} />
);
H2.displayName = "H2";

export const H3 = (props: Omit<TypographyProps, "variant" | "as">) => (
	<Typography variant="h3" {...props} />
);
H3.displayName = "H3";

export const H4 = (props: Omit<TypographyProps, "variant" | "as">) => (
	<Typography variant="h4" {...props} />
);
H4.displayName = "H4";

export const Lead = (props: Omit<TypographyProps, "variant" | "as">) => (
	<Typography variant="lead" {...props} />
);
Lead.displayName = "Lead";

export const P = (props: Omit<TypographyProps, "variant" | "as">) => (
	<Typography variant="paragraph" {...props} />
);
P.displayName = "P";

export const Large = (props: Omit<TypographyProps, "variant" | "as">) => (
	<Typography variant="large" {...props} />
);
Large.displayName = "Large";

export const Small = (props: Omit<TypographyProps, "variant" | "as">) => (
	<Typography variant="small" {...props} />
);
Small.displayName = "Small";

export const Muted = (props: Omit<TypographyProps, "variant" | "as">) => (
	<Typography variant="muted" {...props} />
);
Muted.displayName = "Muted";

export const InlineCode = (props: Omit<TypographyProps, "variant" | "as">) => (
	<Typography variant="inlineCode" {...props} />
);
InlineCode.displayName = "InlineCode";

export const MultilineCode = (
	props: Omit<TypographyProps, "variant" | "as">,
) => <Typography variant="multilineCode" {...props} />;
MultilineCode.displayName = "MultilineCode";

export const List = (props: Omit<TypographyProps, "variant" | "as">) => (
	<Typography variant="list" {...props} />
);
List.displayName = "List";

export const Quote = (props: Omit<TypographyProps, "variant" | "as">) => (
	<Typography variant="quote" {...props} />
);
Quote.displayName = "Quote";

export const Text = (props: Omit<TypographyProps, "variant" | "as">) => (
	<Typography variant="text" {...props} />
);
Text.displayName = "Text";

export { Typography, typographyVariants };
